// Backend integration point: Replace all mock data with API calls to your backend service

export type ProjectStatus =
'draft' | 'awaiting_design' | 'awaiting_quotations' | 'awaiting_approval' | 'awaiting_deposit' | 'active' | 'delayed' | 'disputed' | 'completed' | 'cancelled';

export type QuotationStatus =
'draft' | 'submitted' | 'under_review' | 'shortlisted' | 'accepted' | 'rejected' | 'expired';

export type MilestoneStatus =
'not_started' | 'in_progress' | 'awaiting_payment' | 'awaiting_approval' | 'corrections_requested' | 'completed' | 'delayed' | 'disputed';

export type PaymentStatus =
'not_required' | 'pending' | 'processing' | 'paid' | 'failed' | 'refunded';

export interface Contractor {
  id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  logo: string;
  coverImage: string;
  description: string;
  services: string[];
  locations: string[];
  yearsExperience: number;
  rating: number;
  reviewCount: number;
  completedProjects: number;
  completionRate: number;
  avgProjectValue: number;
  startingPrice: string;
  availability: 'available' | 'busy' | 'unavailable';
  verified: boolean;
  verificationStatus: 'approved' | 'pending' | 'rejected' | 'more_info';
  teamSize: number;
  registrationNumber: string;
  specialities: string[];
  warranties: string;
  portfolio: PortfolioItem[];
}

export interface PortfolioItem {
  id: string;
  title: string;
  image: string;
  category: string;
  value: number;
  duration: string;
}

export interface Client {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  location: string;
  avatar: string;
  joinedDate: string;
  projectCount: number;
}

export interface Milestone {
  id: string;
  projectId: string;
  name: string;
  phase: string;
  description: string;
  dueDate: string;
  amount: number;
  percentage: number;
  workStatus: MilestoneStatus;
  paymentStatus: PaymentStatus;
  approvalStatus: 'pending' | 'approved' | 'corrections_requested' | 'not_submitted';
  notes: string;
  photos: string[];
}

export interface ProgressReport {
  id: string;
  projectId: string;
  title: string;
  period: string;
  phase: string;
  workCompleted: string;
  workPlanned: string;
  completionPercentage: number;
  materialsUsed: string;
  moneyUsed: number;
  budgetRemaining: number;
  issues: string;
  photos: string[];
  submittedDate: string;
  contractorName: string;
}

export interface Expense {
  id: string;
  projectId: string;
  name: string;
  category: string;
  phase: string;
  supplier: string;
  amount: number;
  date: string;
  paymentMethod: string;
  notes: string;
}

export interface ChatMessage {
  id: string;
  projectId: string;
  senderId: string;
  senderRole: 'client' | 'contractor' | 'admin';
  senderName: string;
  content: string;
  timestamp: string;
  read: boolean;
  attachments?: {name: string;type: string;size: string;}[];
}

export interface Product {
  id: string;
  name: string;
  brand: string;
  brandId: string;
  category: string;
  price: number;
  image: string;
  rating: number;
  reviewCount: number;
  availability: 'in_stock' | 'limited' | 'out_of_stock';
  description: string;
  specifications: Record<string, string>;
  suitableFor: string[];
  deliveryEstimate: string;
  warranty: string;
}

export interface ProjectQuotation {
  id: string;
  projectId: string;
  contractorId: string;
  contractorName: string;
  contractorRating: number;
  contractorCompleted: number;
  contractorVerified: boolean;
  status: QuotationStatus;
  subtotal: number;
  platformFee: number;
  total: number;
  depositPercentage: number;
  depositAmount: number;
  estimatedDuration: string;
  proposedStart: string;
  proposedCompletion: string;
  milestoneCount: number;
  warranty: string;
  workDescription: string;
  exclusions: string;
  notes: string;
  submittedDate: string;
}

export interface Project {
  id: string;
  title: string;
  type: string;
  clientId: string;
  clientName: string;
  contractorId: string | null;
  contractorName: string | null;
  location: string;
  status: ProjectStatus;
  currentPhase: string;
  completionPercentage: number;
  contractorSubtotal: number;
  platformFee: number;
  totalValue: number;
  amountPaid: number;
  remainingBalance: number;
  depositPaid: boolean;
  depositAmount: number;
  startDate: string;
  expectedCompletion: string;
  description: string;
  areas: string[];
  milestones: Milestone[];
  progressReports: ProgressReport[];
  expenses: Expense[];
  messages: ChatMessage[];
  quotations: ProjectQuotation[];
  coverImage: string;
  latestProgressImage: string;
  nextMilestone: string;
  budgetVariance: number;
}

// ─── CONTRACTORS ───────────────────────────────────────────────────────────────
export const contractors: Contractor[] = [
{
  id: 'contractor-001',
  companyName: 'Apex Build Solutions',
  contactPerson: 'Marcus T.',
  email: 'Hidden by platform',
  phone: 'Hidden by platform',
  logo: "https://img.rocket.new/generatedImages/rocket_gen_img_16d5bf04d-1773662482997.png",
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_16d5bf04d-1773662482997.png",
  description: 'Award-winning construction firm specialising in full home renovations and new builds across Johannesburg and Gauteng. 15 years of delivering premium residential and commercial projects on time and within budget.',
  services: ['Full Home Renovation', 'New Build', 'Kitchen Renovation', 'Bathroom Renovation', 'Extensions'],
  locations: ['Johannesburg', 'Sandton', 'Midrand', 'Centurion'],
  yearsExperience: 15,
  rating: 4.9,
  reviewCount: 124,
  completedProjects: 187,
  completionRate: 96,
  avgProjectValue: 1088000,
  startingPrice: 'R240,000',
  availability: 'available',
  verified: true,
  verificationStatus: 'approved',
  teamSize: 24,
  registrationNumber: 'ZA-CIDB-2847392',
  specialities: ['Period Property Restoration', 'Open Plan Living', 'Luxury Kitchens'],
  warranties: '10-year structural, 5-year fixtures',
  portfolio: [
  { id: 'port-001', title: 'Sandton Kitchen Transformation', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1da4188b4-1769300661655.png", category: 'Kitchen', value: 720000, duration: '8 weeks' },
  { id: 'port-002', title: 'Rosebank Full Home Renovation', image: "https://img.rocket.new/generatedImages/rocket_gen_img_103b4ce22-1785305733148.png", category: 'Full Renovation', value: 1920000, duration: '24 weeks' }]

},
{
  id: 'contractor-002',
  companyName: 'UrbanCraft Renovations',
  contactPerson: 'Priya S.',
  email: 'Hidden by platform',
  phone: 'Hidden by platform',
  logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd531fb1-1777434505092.png",
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd531fb1-1777434505092.png",
  description: 'Contemporary interiors and renovation specialists with a focus on sustainable materials and modern design. Trusted by over 200 homeowners across Cape Town and the Western Cape.',
  services: ['Interior Design', 'Kitchen Renovation', 'Bathroom Renovation', 'Flooring', 'Painting'],
  locations: ['Cape Town', 'Bellville', 'Stellenbosch', 'Somerset West'],
  yearsExperience: 9,
  rating: 4.7,
  reviewCount: 89,
  completedProjects: 143,
  completionRate: 94,
  avgProjectValue: 512000,
  startingPrice: 'R128,000',
  availability: 'available',
  verified: true,
  verificationStatus: 'approved',
  teamSize: 14,
  registrationNumber: 'ZA-CIDB-1938472',
  specialities: ['Sustainable Builds', 'Contemporary Interiors', 'Smart Home Integration'],
  warranties: '7-year structural, 3-year fixtures',
  portfolio: [
  { id: 'port-003', title: 'Constantia Open Plan Living', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d7b5c5b3-1772131519632.png", category: 'Full Renovation', value: 880000, duration: '14 weeks' }]

},
{
  id: 'contractor-003',
  companyName: 'Horizon Construction Group',
  contactPerson: 'James W.',
  email: 'Hidden by platform',
  phone: 'Hidden by platform',
  logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18bb44380-1785305733038.png",
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18bb44380-1785305733038.png",
  description: 'Major construction and civil engineering contractor operating across KwaZulu-Natal. Specialists in commercial fit-outs, office refurbishments, and large-scale residential developments.',
  services: ['Commercial Construction', 'Office Fit-Out', 'New Build', 'Structural Work', 'Roofing'],
  locations: ['Durban', 'Umhlanga', 'Pinetown', 'Pietermaritzburg', 'Richards Bay'],
  yearsExperience: 22,
  rating: 4.8,
  reviewCount: 211,
  completedProjects: 342,
  completionRate: 97,
  avgProjectValue: 2320000,
  startingPrice: 'R400,000',
  availability: 'busy',
  verified: true,
  verificationStatus: 'approved',
  teamSize: 58,
  registrationNumber: 'ZA-CIDB-0847261',
  specialities: ['Commercial Fit-Out', 'Large Scale Developments', 'Structural Engineering'],
  warranties: '15-year structural, 10-year commercial',
  portfolio: [
  { id: 'port-004', title: 'Umhlanga Ridge Office Refurb', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1631abd56-1785305733815.png", category: 'Commercial', value: 4480000, duration: '32 weeks' }]

},
{
  id: 'contractor-004',
  companyName: 'Prime Interior Works',
  contactPerson: 'Amara O.',
  email: 'Hidden by platform',
  phone: 'Hidden by platform',
  logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea3cf389-1772691748444.png",
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea3cf389-1772691748444.png",
  description: 'Luxury interior specialists serving Pretoria and Tshwane. Known for bespoke kitchen and bathroom installations using premium imported materials and fixtures.',
  services: ['Kitchen Renovation', 'Bathroom Renovation', 'Interior Design', 'Tiling', 'Electrical Work'],
  locations: ['Pretoria', 'Centurion', 'Hatfield', 'Menlyn'],
  yearsExperience: 12,
  rating: 4.6,
  reviewCount: 67,
  completedProjects: 98,
  completionRate: 93,
  avgProjectValue: 656000,
  startingPrice: 'R160,000',
  availability: 'available',
  verified: true,
  verificationStatus: 'approved',
  teamSize: 18,
  registrationNumber: 'ZA-CIDB-2847193',
  specialities: ['Bespoke Kitchens', 'Luxury Bathrooms', 'Period Features'],
  warranties: '8-year structural, 5-year fixtures',
  portfolio: [
  { id: 'port-005', title: 'Waterkloof Luxury Kitchen', image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cd341bb8-1772789951668.png", category: 'Kitchen', value: 992000, duration: '10 weeks' }]

},
{
  id: 'contractor-005',
  companyName: 'SolidStone Builders',
  contactPerson: 'Ryan M.',
  email: 'Hidden by platform',
  phone: 'Hidden by platform',
  logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d58bed22-1785245828067.png",
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d58bed22-1785245828067.png",
  description: 'Structural building specialists with expertise in extensions, loft conversions, and new builds. Serving Gqeberha and the Eastern Cape with a reputation for quality brickwork and structural integrity.',
  services: ['Room Extension', 'Loft Conversion', 'New Build', 'Structural Work', 'Roofing'],
  locations: ['Gqeberha', 'Uitenhage', 'George', 'Knysna'],
  yearsExperience: 18,
  rating: 4.8,
  reviewCount: 156,
  completedProjects: 234,
  completionRate: 95,
  avgProjectValue: 1248000,
  startingPrice: 'R320,000',
  availability: 'available',
  verified: true,
  verificationStatus: 'approved',
  teamSize: 32,
  registrationNumber: 'ZA-CIDB-1726483',
  specialities: ['Loft Conversions', 'Extensions', 'Structural Brickwork'],
  warranties: '12-year structural, 7-year fixtures',
  portfolio: [
  { id: 'port-006', title: 'Summerstrand Double Extension', image: "https://images.unsplash.com/photo-1594422248115-c141458c1426", category: 'Extension', value: 1520000, duration: '20 weeks' }]

},
{
  id: 'contractor-006',
  companyName: 'Modern Space Contractors',
  contactPerson: 'Elena K.',
  email: 'Hidden by platform',
  phone: 'Hidden by platform',
  logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fefb2219-1785305732909.png",
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fefb2219-1785305732909.png",
  description: 'Contemporary design and build studio specialising in open-plan transformations, smart home integration, and modern interiors. Based in Johannesburg with projects across Gauteng.',
  services: ['Interior Design', 'Full Home Renovation', 'Ceiling Installation', 'Flooring', 'Painting'],
  locations: ['Johannesburg', 'Fourways', 'Randburg', 'Roodepoort'],
  yearsExperience: 7,
  rating: 4.9,
  reviewCount: 43,
  completedProjects: 67,
  completionRate: 98,
  avgProjectValue: 608000,
  startingPrice: 'R192,000',
  availability: 'available',
  verified: true,
  verificationStatus: 'approved',
  teamSize: 11,
  registrationNumber: 'ZA-CIDB-3847291',
  specialities: ['Contemporary Design', 'Smart Home', 'Open Plan Living'],
  warranties: '5-year structural, 3-year fixtures',
  portfolio: [
  { id: 'port-007', title: 'Melrose Arch Home Transformation', image: "https://img.rocket.new/generatedImages/rocket_gen_img_196779167-1785305732187.png", category: 'Full Renovation', value: 768000, duration: '12 weeks' }]

}];


// ─── CLIENTS ───────────────────────────────────────────────────────────────────
export const clients: Client[] = [
{ id: 'client-001', fullName: 'Sarah Mitchell', email: 'client@demo.com', phone: 'Hidden by platform', location: 'Sandton, Johannesburg', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=80&h=80&fit=crop&face', joinedDate: '2026-01-15', projectCount: 3 },
{ id: 'client-002', fullName: 'David Chen', email: 'david.chen@demo.com', phone: 'Hidden by platform', location: 'Sea Point, Cape Town', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop', joinedDate: '2025-11-03', projectCount: 2 },
{ id: 'client-003', fullName: 'Fatima Al-Rashid', email: 'fatima@demo.com', phone: 'Hidden by platform', location: 'Hatfield, Pretoria', avatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&h=80&fit=crop', joinedDate: '2025-12-20', projectCount: 1 },
{ id: 'client-004', fullName: 'Tom Harrington', email: 'tom@demo.com', phone: 'Hidden by platform', location: 'Umhlanga, Durban', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop', joinedDate: '2026-02-08', projectCount: 2 },
{ id: 'client-005', fullName: 'Yemi Adeyemi', email: 'yemi@demo.com', phone: 'Hidden by platform', location: 'Rosebank, Johannesburg', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop', joinedDate: '2026-03-14', projectCount: 1 },
{ id: 'client-006', fullName: 'Claire Beaumont', email: 'claire@demo.com', phone: 'Hidden by platform', location: 'Stellenbosch, Western Cape', avatar: "https://images.unsplash.com/photo-1701622420355-aabbe1901e81", joinedDate: '2026-04-02', projectCount: 1 }];


// ─── MILESTONES ─────────────────────────────────────────────────────────────────
export const kitchenMilestones: Milestone[] = [
{ id: 'ms-001', projectId: 'project-001', name: 'Deposit Payment', phase: 'Planning & Design', description: 'Initial deposit to commence project planning and procurement.', dueDate: '2026-02-15', amount: 110400, percentage: 20, workStatus: 'completed', paymentStatus: 'paid', approvalStatus: 'approved', notes: 'Deposit received and project workspace unlocked.', photos: [] },
{ id: 'ms-002', projectId: 'project-001', name: 'Demolition & Site Prep', phase: 'Demolition', description: 'Remove existing kitchen units, strip walls, prepare subfloor.', dueDate: '2026-03-01', amount: 82800, percentage: 15, workStatus: 'completed', paymentStatus: 'paid', approvalStatus: 'approved', notes: 'Demolition completed. Structural wall confirmed load-bearing — RSJ required.', photos: ['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop'] },
{ id: 'ms-003', projectId: 'project-001', name: 'Structural & Electrical First Fix', phase: 'Structural Work', description: 'RSJ installation, first fix electrical rewire, plumbing rough-in.', dueDate: '2026-03-20', amount: 110400, percentage: 20, workStatus: 'completed', paymentStatus: 'paid', approvalStatus: 'approved', notes: 'RSJ installed. Electrical first fix complete. Building inspector approved.', photos: ['https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop'] },
{ id: 'ms-004', projectId: 'project-001', name: 'Kitchen Unit Installation', phase: 'Kitchen Installation', description: 'Install base units, wall units, worktops, and appliances.', dueDate: '2026-04-10', amount: 138000, percentage: 25, workStatus: 'awaiting_approval', paymentStatus: 'pending', approvalStatus: 'not_submitted', notes: 'All units installed. Awaiting client sign-off before tiling begins.', photos: ['https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop', "https://img.rocket.new/generatedImages/rocket_gen_img_1a9c7e50d-1772864005909.png"] },
{ id: 'ms-005', projectId: 'project-001', name: 'Tiling, Painting & Final Fit', phase: 'Painting & Finishes', description: 'Splashback tiling, painting, final electrical fit, snagging.', dueDate: '2026-04-28', amount: 110400, percentage: 20, workStatus: 'not_started', paymentStatus: 'not_required', approvalStatus: 'not_submitted', notes: '', photos: [] }];


export const bathroomMilestones: Milestone[] = [
{ id: 'ms-006', projectId: 'project-002', name: 'Deposit Payment', phase: 'Planning & Design', description: 'Initial deposit to commence project.', dueDate: '2026-03-01', amount: 55200, percentage: 20, workStatus: 'completed', paymentStatus: 'paid', approvalStatus: 'approved', notes: '', photos: [] },
{ id: 'ms-007', projectId: 'project-002', name: 'Strip Out & Waterproofing', phase: 'Demolition', description: 'Remove existing fittings, tanking and waterproof walls and floor.', dueDate: '2026-03-18', amount: 41400, percentage: 15, workStatus: 'completed', paymentStatus: 'paid', approvalStatus: 'approved', notes: '', photos: [] },
{ id: 'ms-008', projectId: 'project-002', name: 'Plumbing & Tiling', phase: 'Plumbing', description: 'Install new plumbing, tile walls and floor.', dueDate: '2026-04-05', amount: 69000, percentage: 25, workStatus: 'in_progress', paymentStatus: 'pending', approvalStatus: 'not_submitted', notes: 'Tiling 60% complete.', photos: [] },
{ id: 'ms-009', projectId: 'project-002', name: 'Fixtures & Final Fit', phase: 'Bathroom Installation', description: 'Install sanitaryware, taps, shower, accessories, and final paint.', dueDate: '2026-04-22', amount: 55200, percentage: 20, workStatus: 'not_started', paymentStatus: 'not_required', approvalStatus: 'not_submitted', notes: '', photos: [] },
{ id: 'ms-010', projectId: 'project-002', name: 'Final Payment', phase: 'Final Handover', description: 'Snagging complete, final sign-off.', dueDate: '2026-04-30', amount: 55200, percentage: 20, workStatus: 'not_started', paymentStatus: 'not_required', approvalStatus: 'not_submitted', notes: '', photos: [] }];


// ─── PROGRESS REPORTS ──────────────────────────────────────────────────────────
export const kitchenProgressReports: ProgressReport[] = [
{
  id: 'pr-001', projectId: 'project-001', title: 'Week 8 Progress — Kitchen Unit Installation', period: '14 Apr – 20 Apr 2026', phase: 'Kitchen Installation', workCompleted: 'All base and wall units installed. Granite worktops cut and fitted. Integrated appliances (dishwasher, oven, fridge) installed and operational. Sink plumbed in.', workPlanned: 'Splashback tiling to commence Monday. Electrical second fix (sockets, under-cabinet lighting). Touch-up painting.', completionPercentage: 78, materialsUsed: 'Miele kitchen units, Absolute Black Granite worktops, Samsung integrated appliances', moneyUsed: 441600, budgetRemaining: 110400, issues: 'Minor delay on worktop delivery — 2 days. No impact on overall timeline.', photos: ['https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop', 'https://images.unsplash.com/photo-1556909172-54557c7e4fb7?w=600&h=400&fit=crop', 'https://images.unsplash.com/photo-1556909048-f5d5b9e88e08?w=600&h=400&fit=crop'], submittedDate: '2026-04-20', contractorName: 'Apex Build Solutions'
},
{
  id: 'pr-002', projectId: 'project-001', title: 'Week 5 Progress — Structural Work Complete', period: '24 Mar – 30 Mar 2026', phase: 'Structural Work', workCompleted: 'RSJ beam installed and propped. First fix electrical rewire of kitchen zone complete. Plumbing rough-in for sink and dishwasher. Building inspector sign-off obtained.', workPlanned: 'Plasterboard and skim walls. Floor levelling compound. Unit delivery and installation.', completionPercentage: 52, materialsUsed: 'Structural steel RSJ 203x133, 25m twin & earth 6mm cable, 22mm copper pipe', moneyUsed: 303600, budgetRemaining: 248400, issues: 'None.', photos: ['https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=400&fit=crop'], submittedDate: '2026-03-30', contractorName: 'Apex Build Solutions'
}];


// ─── EXPENSES ──────────────────────────────────────────────────────────────────
export const kitchenExpenses: Expense[] = [
{ id: 'exp-001', projectId: 'project-001', name: 'Kitchen Units', category: 'Materials', phase: 'Kitchen Installation', supplier: 'Miele SA', amount: 134400, date: '2026-04-02', paymentMethod: 'EFT', notes: 'Full unit order — 18 base, 12 wall units' },
{ id: 'exp-002', projectId: 'project-001', name: 'Granite Worktops', category: 'Materials', phase: 'Kitchen Installation', supplier: 'Stone Direct SA', amount: 51200, date: '2026-04-08', paymentMethod: 'EFT', notes: '4.2m run Absolute Black Granite' },
{ id: 'exp-003', projectId: 'project-001', name: 'Samsung Integrated Appliances Pack', category: 'Materials', phase: 'Kitchen Installation', supplier: "Hirsch's Appliances", amount: 45440, date: '2026-04-01', paymentMethod: 'Card', notes: 'Oven, hob, dishwasher, fridge-freezer' },
{ id: 'exp-004', projectId: 'project-001', name: 'RSJ Structural Steel', category: 'Materials', phase: 'Structural Work', supplier: 'Steel Supplies SA', amount: 19200, date: '2026-03-14', paymentMethod: 'EFT', notes: '203x133 UB steel beam 4.8m' },
{ id: 'exp-005', projectId: 'project-001', name: 'Electrician — First Fix', category: 'Labour', phase: 'Structural Work', supplier: 'Sparks Electrical', amount: 28800, date: '2026-03-22', paymentMethod: 'EFT', notes: 'Subcontractor — full kitchen rewire' },
{ id: 'exp-006', projectId: 'project-001', name: 'Skip Hire — Demolition Waste', category: 'Transport', phase: 'Demolition', supplier: 'ClearWaste SA', amount: 5440, date: '2026-02-28', paymentMethod: 'Card', notes: '8 yard skip, 2 weeks' },
{ id: 'exp-007', projectId: 'project-001', name: 'Building Inspector Fee', category: 'Permits', phase: 'Structural Work', supplier: 'City of Johannesburg', amount: 2880, date: '2026-03-25', paymentMethod: 'Card', notes: 'Structural alteration sign-off' }];


// ─── CHAT MESSAGES ─────────────────────────────────────────────────────────────
export const kitchenMessages: ChatMessage[] = [
{ id: 'msg-001', projectId: 'project-001', senderId: 'contractor-001', senderRole: 'contractor', senderName: 'Marcus T. (Apex Build)', content: 'Good morning Sarah. Units are all in and looking great. I\'ll send the week 8 progress report this afternoon with photos. Are you free to visit site on Thursday to give sign-off before we start tiling?', timestamp: '2026-04-20T09:14:00', read: true },
{ id: 'msg-002', projectId: 'project-001', senderId: 'client-001', senderRole: 'client', senderName: 'Sarah M.', content: 'Morning Marcus! That\'s brilliant news. Thursday works perfectly — I can be there from 10am. Will the electrician be on site too so I can see the socket positions?', timestamp: '2026-04-20T09:47:00', read: true },
{ id: 'msg-003', projectId: 'project-001', senderId: 'contractor-001', senderRole: 'contractor', senderName: 'Marcus T. (Apex Build)', content: 'Yes, Dan the electrician will be there from 9am. He can walk you through the socket layout and under-cabinet lighting positions before you arrive. See you Thursday!', timestamp: '2026-04-20T10:02:00', read: true },
{ id: 'msg-004', projectId: 'project-001', senderId: 'client-001', senderRole: 'client', senderName: 'Sarah M.', content: 'Perfect. Also — I saw the worktop samples yesterday. Can we go with the Absolute Black Granite rather than the Nero Marquina? I know it\'s what we discussed originally but I\'d like to confirm.', timestamp: '2026-04-20T11:30:00', read: true },
{ id: 'msg-005', projectId: 'project-001', senderId: 'contractor-001', senderRole: 'contractor', senderName: 'Marcus T. (Apex Build)', content: 'Absolutely — Absolute Black is the better choice for your cabinet colour. No price difference on this order. Confirmed ✓', timestamp: '2026-04-20T11:58:00', read: false }];


// ─── QUOTATIONS ────────────────────────────────────────────────────────────────
export const projectQuotations: ProjectQuotation[] = [
{
  id: 'quote-001', projectId: 'project-001', contractorId: 'contractor-001', contractorName: 'Apex Build Solutions', contractorRating: 4.9, contractorCompleted: 187, contractorVerified: true, status: 'accepted', subtotal: 480000, platformFee: 72000, total: 552000, depositPercentage: 20, depositAmount: 110400, estimatedDuration: '10 weeks', proposedStart: '2026-02-20', proposedCompletion: '2026-04-28', milestoneCount: 5, warranty: '10-year structural, 5-year fixtures', workDescription: 'Complete kitchen renovation including demolition, structural alterations, full rewire, plumbing, premium unit installation, and all finishes.', exclusions: 'New geyser installation, external gas supply alterations', notes: 'Price includes all materials. Premium kitchen units and granite worktops specified.', submittedDate: '2026-02-01'
},
{
  id: 'quote-002', projectId: 'project-001', contractorId: 'contractor-004', contractorName: 'Prime Interior Works', contractorRating: 4.6, contractorCompleted: 98, contractorVerified: true, status: 'rejected', subtotal: 416000, platformFee: 62400, total: 478400, depositPercentage: 25, depositAmount: 119600, estimatedDuration: '12 weeks', proposedStart: '2026-03-01', proposedCompletion: '2026-05-22', milestoneCount: 4, warranty: '8-year structural, 5-year fixtures', workDescription: 'Full kitchen renovation with premium finishes.', exclusions: 'Geyser works, external gas', notes: 'Using imported German units. Longer timeline due to current workload.', submittedDate: '2026-02-03'
},
{
  id: 'quote-003', projectId: 'project-001', contractorId: 'contractor-002', contractorName: 'UrbanCraft Renovations', contractorRating: 4.7, contractorCompleted: 143, contractorVerified: true, status: 'rejected', subtotal: 352000, platformFee: 52800, total: 404800, depositPercentage: 20, depositAmount: 80960, estimatedDuration: '8 weeks', proposedStart: '2026-02-25', proposedCompletion: '2026-04-21', milestoneCount: 4, warranty: '7-year structural, 3-year fixtures', workDescription: 'Kitchen renovation with mid-range units and sustainable materials.', exclusions: 'Structural alterations, gas works', notes: 'Sustainable material options available at no extra cost.', submittedDate: '2026-02-02'
}];


// ─── PROJECTS ──────────────────────────────────────────────────────────────────
export const projects: Project[] = [
{
  id: 'project-001',
  title: 'Complete Kitchen Renovation',
  type: 'Kitchen Renovation',
  clientId: 'client-001',
  clientName: 'Sarah M.',
  contractorId: 'contractor-001',
  contractorName: 'Apex Build Solutions',
  location: 'Sandton, Johannesburg',
  status: 'active',
  currentPhase: 'Kitchen Installation',
  completionPercentage: 78,
  contractorSubtotal: 480000,
  platformFee: 72000,
  totalValue: 552000,
  amountPaid: 441600,
  remainingBalance: 110400,
  depositPaid: true,
  depositAmount: 110400,
  startDate: '2026-02-20',
  expectedCompletion: '2026-04-28',
  description: 'Full kitchen renovation including structural wall removal, open-plan extension, premium kitchen units with granite worktops, integrated Samsung appliances, and complete rewire.',
  areas: ['Kitchen', 'Dining Room'],
  milestones: kitchenMilestones,
  progressReports: kitchenProgressReports,
  expenses: kitchenExpenses,
  messages: kitchenMessages,
  quotations: projectQuotations,
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c208df4f-1772984509475.png",
  latestProgressImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9c7e50d-1772864005909.png",
  nextMilestone: 'Kitchen Unit Installation — R138,000 due',
  budgetVariance: -2.3
},
{
  id: 'project-002',
  title: 'Main Bathroom Upgrade',
  type: 'Bathroom Renovation',
  clientId: 'client-001',
  clientName: 'Sarah M.',
  contractorId: 'contractor-002',
  contractorName: 'UrbanCraft Renovations',
  location: 'Sandton, Johannesburg',
  status: 'active',
  currentPhase: 'Plumbing & Tiling',
  completionPercentage: 45,
  contractorSubtotal: 240000,
  platformFee: 36000,
  totalValue: 276000,
  amountPaid: 96600,
  remainingBalance: 179400,
  depositPaid: true,
  depositAmount: 55200,
  startDate: '2026-03-01',
  expectedCompletion: '2026-04-30',
  description: 'Complete bathroom renovation with new suite, walk-in shower, heated towel rail, underfloor heating, and premium wall and floor tiling.',
  areas: ['Bathroom'],
  milestones: bathroomMilestones,
  progressReports: [],
  expenses: [],
  messages: [],
  quotations: [],
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f23a8511-1772792478015.png",
  latestProgressImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f23a8511-1772792478015.png",
  nextMilestone: 'Plumbing & Tiling — R69,000 due',
  budgetVariance: 1.1
},
{
  id: 'project-003',
  title: 'Three-Bedroom Home Extension',
  type: 'Room Extension',
  clientId: 'client-004',
  clientName: 'Tom H.',
  contractorId: 'contractor-005',
  contractorName: 'SolidStone Builders',
  location: 'Umhlanga, Durban',
  status: 'delayed',
  currentPhase: 'Structural Work',
  completionPercentage: 38,
  contractorSubtotal: 1520000,
  platformFee: 228000,
  totalValue: 1748000,
  amountPaid: 699200,
  remainingBalance: 1048800,
  depositPaid: true,
  depositAmount: 349600,
  startDate: '2025-12-01',
  expectedCompletion: '2026-06-30',
  description: 'Side and rear double extension to create additional bedroom, enlarged kitchen-diner, and home office.',
  areas: ['Kitchen', 'Dining Room', 'Bedroom', 'Office'],
  milestones: [],
  progressReports: [],
  expenses: [],
  messages: [],
  quotations: [],
  coverImage: "https://images.unsplash.com/photo-1594719072263-7ab9ab79ec8d",
  latestProgressImage: "https://images.unsplash.com/photo-1594719072263-7ab9ab79ec8d",
  nextMilestone: 'Roof Structure — R308,000 due',
  budgetVariance: 8.4
},
{
  id: 'project-004',
  title: 'Office Interior Fit-Out',
  type: 'Office Fit-Out',
  clientId: 'client-002',
  clientName: 'David C.',
  contractorId: 'contractor-003',
  contractorName: 'Horizon Construction Group',
  location: 'Umhlanga Ridge, Durban',
  status: 'completed',
  currentPhase: 'Final Handover',
  completionPercentage: 100,
  contractorSubtotal: 768000,
  platformFee: 115200,
  totalValue: 883200,
  amountPaid: 883200,
  remainingBalance: 0,
  depositPaid: true,
  depositAmount: 176640,
  startDate: '2025-09-15',
  expectedCompletion: '2025-12-20',
  description: 'Complete office fit-out for 40-person workspace including partitioning, flooring, electrical, IT infrastructure, kitchen, and meeting rooms.',
  areas: ['Office', 'Kitchen', 'Meeting Rooms'],
  milestones: [],
  progressReports: [],
  expenses: [],
  messages: [],
  quotations: [],
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1298fd500-1773514507178.png",
  latestProgressImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1298fd500-1773514507178.png",
  nextMilestone: 'None — Project Complete',
  budgetVariance: -1.2
},
{
  id: 'project-005',
  title: 'Living Room Ceiling Installation',
  type: 'Ceiling Installation',
  clientId: 'client-001',
  clientName: 'Sarah M.',
  contractorId: null,
  contractorName: null,
  location: 'Sandton, Johannesburg',
  status: 'awaiting_quotations',
  currentPhase: 'Awaiting Quotations',
  completionPercentage: 0,
  contractorSubtotal: 0,
  platformFee: 0,
  totalValue: 0,
  amountPaid: 0,
  remainingBalance: 0,
  depositPaid: false,
  depositAmount: 0,
  startDate: '',
  expectedCompletion: '',
  description: 'Install new decorative coffered ceiling in living room with integrated LED lighting.',
  areas: ['Living Room'],
  milestones: [],
  progressReports: [],
  expenses: [],
  messages: [],
  quotations: [],
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_12c4c3cf7-1775442719711.png",
  latestProgressImage: '',
  nextMilestone: 'Awaiting quotations from contractors',
  budgetVariance: 0
},
{
  id: 'project-006',
  title: 'New Family Home Construction',
  type: 'New Home Construction',
  clientId: 'client-005',
  clientName: 'Yemi A.',
  contractorId: 'contractor-003',
  contractorName: 'Horizon Construction Group',
  location: 'Waterfall Estate, Midrand',
  status: 'active',
  currentPhase: 'Structural Work',
  completionPercentage: 28,
  contractorSubtotal: 5120000,
  platformFee: 768000,
  totalValue: 5888000,
  amountPaid: 1766400,
  remainingBalance: 4121600,
  depositPaid: true,
  depositAmount: 1177600,
  startDate: '2026-01-10',
  expectedCompletion: '2026-12-31',
  description: '4-bedroom detached family home — full new build including foundations, structural frame, roofing, all services, internal fit-out, and landscaping.',
  areas: ['Entire Property'],
  milestones: [],
  progressReports: [],
  expenses: [],
  messages: [],
  quotations: [],
  coverImage: "https://images.unsplash.com/photo-1719859798083-7ad7cc23eafc",
  latestProgressImage: "https://images.unsplash.com/photo-1719859798083-7ad7cc23eafc",
  nextMilestone: 'Roof Frame Complete — R588,800 due',
  budgetVariance: 2.1
}];


// ─── PRODUCTS ──────────────────────────────────────────────────────────────────
export const products: Product[] = [
{ id: 'prod-001', name: 'Quooker Fusion Round Boiling Tap', brand: 'Quooker', brandId: 'brand-001', category: 'Taps', price: 20784, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4215de0-1785245881470.png", rating: 4.8, reviewCount: 234, availability: 'in_stock', description: 'Instant boiling, chilled and sparkling water from one tap. 3-in-1 PRO3 fusion system.', specifications: { 'Flow Rate': '8L/min', 'Voltage': '220V', 'Dimensions': '210mm H × 175mm W' }, suitableFor: ['Kitchen Renovation', 'New Build'], deliveryEstimate: '5-7 working days', warranty: '2 years' },
{ id: 'prod-002', name: 'Duravit ME by Starck Rimless Toilet', brand: 'Duravit', brandId: 'brand-002', category: 'Toilets', price: 10384, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a26dbc7c-1785245881488.png", rating: 4.7, reviewCount: 156, availability: 'in_stock', description: 'Wall-hung rimless WC designed by Philippe Starck. HygieneGlaze coating for effortless cleaning.', specifications: { 'Dimensions': '570 × 370mm', 'Weight': '23kg', 'Flush': '4.5/3L dual flush' }, suitableFor: ['Bathroom Renovation', 'New Build'], deliveryEstimate: '7-10 working days', warranty: '5 years' },
{ id: 'prod-003', name: 'Porcelanosa Venis Ferroker Titanio Floor Tile', brand: 'Porcelanosa', brandId: 'brand-003', category: 'Tiles', price: 1424, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ddf2eccb-1774500777009.png", rating: 4.6, reviewCount: 89, availability: 'in_stock', description: 'Industrial-look large format porcelain tile. 120×60cm format. Suitable for floor and wall.', specifications: { 'Size': '120 × 60cm', 'Thickness': '10mm', 'Finish': 'Matt' }, suitableFor: ['Kitchen Renovation', 'Bathroom Renovation', 'Flooring'], deliveryEstimate: '10-14 working days', warranty: 'Lifetime' },
{ id: 'prod-004', name: 'Grohe Rainshower SmartControl 360 Duo', brand: 'Grohe', brandId: 'brand-004', category: 'Taps', price: 14384, image: "https://images.unsplash.com/photo-1696283807146-b71201b47086", rating: 4.9, reviewCount: 312, availability: 'in_stock', description: 'Smart shower system with intuitive push-and-turn controls. Includes overhead and hand shower.', specifications: { 'Spray types': '3', 'Pressure': '1-5 bar', 'Material': 'Brass/Stainless' }, suitableFor: ['Bathroom Renovation', 'New Build'], deliveryEstimate: '3-5 working days', warranty: '3 years' },
{ id: 'prod-005', name: 'Neff N90 Slide & Hide Built-in Oven', brand: 'Neff', brandId: 'brand-005', category: 'Ovens', price: 18384, image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b061f29-1785245880929.png", rating: 4.8, reviewCount: 178, availability: 'in_stock', description: 'Unique Slide & Hide door for easy access. CircoTherm® hot air system. 71L capacity.', specifications: { 'Capacity': '71L', 'Programmes': '14', 'Energy Rating': 'A+' }, suitableFor: ['Kitchen Renovation', 'New Build'], deliveryEstimate: '5-7 working days', warranty: '2 years' },
{ id: 'prod-006', name: 'Karndean LooseLay Longboard LLP103 Pale Limed Oak', brand: 'Karndean', brandId: 'brand-006', category: 'Flooring', price: 608, image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ddda998-1785245880706.png", rating: 4.7, reviewCount: 445, availability: 'in_stock', description: 'Premium LVT loose lay plank. No adhesive required. 1500×250mm format. Underfloor heating compatible.', specifications: { 'Size': '1500 × 250mm', 'Thickness': '4.5mm', 'Wear Layer': '0.55mm' }, suitableFor: ['Flooring', 'Kitchen Renovation', 'Full Home Renovation'], deliveryEstimate: '3-5 working days', warranty: 'Lifetime residential' },
{ id: 'prod-007', name: 'Villeroy & Boch Architectura MetalRim Bathtub', brand: 'Villeroy & Boch', brandId: 'brand-002', category: 'Bathtubs', price: 29600, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bbf703e0-1768347602519.png", rating: 4.9, reviewCount: 67, availability: 'limited', description: 'Freestanding oval bathtub in Quaryl® material with chrome metal rim. 1800×800mm.', specifications: { 'Dimensions': '1800 × 800mm', 'Material': 'Quaryl®', 'Capacity': '240L' }, suitableFor: ['Bathroom Renovation', 'New Build'], deliveryEstimate: '14-21 working days', warranty: '30 years on body' },
{ id: 'prod-008', name: "Plascon Double Velvet — Elephant's Breath", brand: 'Plascon', brandId: 'brand-006', category: 'Paint', price: 1040, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecc893ee-1783511465116.png", rating: 4.8, reviewCount: 892, availability: 'in_stock', description: 'Premium water-based emulsion with exceptional depth of colour. 5L tin covers approx. 35m².', specifications: { 'Coverage': '35m² per 5L', 'Finish': 'Double Velvet', 'Coats': '2 recommended' }, suitableFor: ['Painting', 'Full Home Renovation', 'Interior Design'], deliveryEstimate: '2-3 working days', warranty: '1 year colour guarantee' },
{ id: 'prod-009', name: 'Velux GGU 0070 Centre-Pivot Roof Window', brand: 'Velux', brandId: 'brand-005', category: 'Windows', price: 7120, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef12edb4-1785245880919.png", rating: 4.7, reviewCount: 234, availability: 'in_stock', description: 'White polyurethane centre-pivot roof window. Triple glazed. Energy rated A.', specifications: { 'Size': '78 × 98cm', 'Glazing': 'Triple', 'Energy Rating': 'A' }, suitableFor: ['Roofing', 'Loft Conversion', 'New Build'], deliveryEstimate: '5-7 working days', warranty: '20 years' },
{ id: 'prod-010', name: 'Franke Mythos Fragranite Sink — Onyx', brand: 'Franke', brandId: 'brand-001', category: 'Sinks', price: 6224, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbd217c9-1785245883376.png", rating: 4.6, reviewCount: 145, availability: 'in_stock', description: '1.5 bowl undermount sink in Fragranite composite. Heat and scratch resistant. 860×500mm.', specifications: { 'Dimensions': '860 × 500mm', 'Material': 'Fragranite', 'Bowls': '1.5' }, suitableFor: ['Kitchen Renovation', 'New Build'], deliveryEstimate: '5-7 working days', warranty: '10 years' },
{ id: 'prod-011', name: 'Hansgrohe Axor Montreux Basin Mixer', brand: 'Hansgrohe', brandId: 'brand-004', category: 'Taps', price: 7760, image: "https://img.rocket.new/generatedImages/rocket_gen_img_15f1a9aa2-1767112895730.png", rating: 4.8, reviewCount: 98, availability: 'in_stock', description: 'Classic cross-handle basin mixer in polished chrome. Ceramic disc technology.', specifications: { 'Finish': 'Chrome', 'Height': '215mm', 'Flow': 'Single lever' }, suitableFor: ['Bathroom Renovation', 'New Build'], deliveryEstimate: '3-5 working days', warranty: '5 years' },
{ id: 'prod-012', name: 'Swartland Heritage Oak 4-Panel Interior Door', brand: 'Swartland', brandId: 'brand-006', category: 'Doors', price: 2864, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f30c3750-1785245880364.png", rating: 4.5, reviewCount: 567, availability: 'in_stock', description: 'Solid core oak veneer 4-panel door. Fire door rated. 1981×762mm standard size.', specifications: { 'Dimensions': '1981 × 762mm', 'Core': 'Solid', 'Rating': 'FD30 fire door' }, suitableFor: ['Full Home Renovation', 'New Build', 'Interior Design'], deliveryEstimate: '7-10 working days', warranty: '5 years' }];


// ─── NOTIFICATIONS ─────────────────────────────────────────────────────────────
export interface Notification {
  id: string;
  userId: string;
  role: 'client' | 'contractor' | 'admin';
  type: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  projectId?: string;
  actionLabel?: string;
}

export const notifications: Notification[] = [
{ id: 'notif-001', userId: 'client-001', role: 'client', type: 'milestone_approval', title: 'Milestone Approval Required', message: 'Kitchen Unit Installation milestone is ready for your review and approval.', timestamp: '2026-04-20T10:00:00', read: false, projectId: 'project-001', actionLabel: 'Review Milestone' },
{ id: 'notif-002', userId: 'client-001', role: 'client', type: 'progress_report', title: 'New Progress Report', message: 'Apex Build Solutions submitted Week 8 progress report for Complete Kitchen Renovation.', timestamp: '2026-04-20T09:30:00', read: false, projectId: 'project-001', actionLabel: 'View Report' },
{ id: 'notif-003', userId: 'client-001', role: 'client', type: 'message', title: 'New Message', message: 'Marcus T. sent you a message on Complete Kitchen Renovation.', timestamp: '2026-04-20T11:58:00', read: false, projectId: 'project-001', actionLabel: 'View Message' },
{ id: 'notif-004', userId: 'contractor-001', role: 'contractor', type: 'quote_request', title: 'New Quotation Request', message: 'Sarah M. has requested a quotation for Living Room Ceiling Installation in Sandton, Johannesburg.', timestamp: '2026-04-19T14:20:00', read: false, projectId: 'project-005', actionLabel: 'Review Request' },
{ id: 'notif-005', userId: 'contractor-001', role: 'contractor', type: 'milestone_payment', title: 'Milestone Payment Received', message: 'Payment of R82,800 received for Demolition & Site Prep milestone on Complete Kitchen Renovation.', timestamp: '2026-03-02T09:00:00', read: true, projectId: 'project-001' }];


// ─── PLATFORM STATS (for Admin Dashboard) ─────────────────────────────────────
export const platformStats = {
  totalClients: 6,
  totalContractors: 8,
  contractorsAwaitingApproval: 3,
  activeProjects: 6,
  delayedProjects: 2,
  completedProjects: 4,
  disputedProjects: 1,
  totalProjectValue: 10947200,
  totalClientPayments: 3888592,
  platformFeesEarned: 1220400,
  pendingPlatformFees: 424800,
  openDisputes: 1
};

// ─── DEMO CREDENTIALS ──────────────────────────────────────────────────────────
export const demoCredentials = {
  client: { email: 'client@demo.com', password: 'demo123', role: 'client' as const },
  contractor: { email: 'contractor@demo.com', password: 'demo123', role: 'contractor' as const },
  admin: { email: 'admin@demo.com', password: 'demo123', role: 'admin' as const }
};

export type UserRole = 'client' | 'contractor' | 'admin';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
}

export const demoUsers: Record<string, AuthUser> = {
  'client@demo.com': { id: 'client-001', name: 'Sarah Mitchell', email: 'client@demo.com', role: 'client', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=80&h=80&fit=crop&face' },
  'contractor@demo.com': { id: 'contractor-001', name: 'Marcus T. — Apex Build Solutions', email: 'contractor@demo.com', role: 'contractor', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d70f6fda-1763296765770.png" },
  'admin@demo.com': { id: 'admin-001', name: 'Platform Administrator', email: 'admin@demo.com', role: 'admin', avatar: '' }
};

// ─── PROJECT CHART DATA ─────────────────────────────────────────────────────────
export const projectValueChartData = [
{ month: 'Aug', value: 720000, fees: 108000 },
{ month: 'Sep', value: 1792000, fees: 268800 },
{ month: 'Oct', value: 1392000, fees: 208800 },
{ month: 'Nov', value: 3168000, fees: 475200 },
{ month: 'Dec', value: 2288000, fees: 343200 },
{ month: 'Jan', value: 4272000, fees: 640800 },
{ month: 'Feb', value: 4992000, fees: 748800 },
{ month: 'Mar', value: 6224000, fees: 933600 },
{ month: 'Apr', value: 6736000, fees: 1010400 }];


export const milestoneStatusData = [
{ name: 'Completed', value: 14, color: 'var(--success)' },
{ name: 'In Progress', value: 8, color: 'var(--primary)' },
{ name: 'Awaiting Payment', value: 5, color: 'var(--accent)' },
{ name: 'Not Started', value: 11, color: 'var(--muted-foreground)' },
{ name: 'Delayed', value: 3, color: 'var(--destructive)' }];


export const budgetData = [
{ phase: 'Planning', allocated: 32000, actual: 29600 },
{ phase: 'Demolition', allocated: 56000, actual: 61440 },
{ phase: 'Structural', allocated: 112000, actual: 114880 },
{ phase: 'Electrical', allocated: 72000, actual: 67200 },
{ phase: 'Plumbing', allocated: 48000, actual: 46400 },
{ phase: 'Kitchen', allocated: 192000, actual: 191360 },
{ phase: 'Finishes', allocated: 64000, actual: 0 }];